import { useEffect, useMemo, useRef, useState } from 'react';
import { useLogBIEvent } from '~/components/BiFrontendProvider/useLogBIEvent';
import { useIoContext } from '~/contexts';
import { useClients } from '~/hooks/reactQuery/queries/useClients';
import i18next from '~/i18n';
import { OPPORTUNITY_PREFIX_SIZE, PULSE_OPPORTUNITIES_ENTITY_NAME, PULSE_OPPORTUNITIES_VIEW_EVENT_TYPE } from '../PulseOpportunitiesWidget.constants';
import { derivePulseOpportunitiesState } from '../utils/derivePulseOpportunitiesState';
import { partitionByRecognisedKind, selectVisibleEntries } from '../utils/opportunityFeedView';
import { unrecognisedKindReport } from '../utils/unrecognisedKindReport';
import { usePulseOpportunities } from './usePulseOpportunities';
const NO_OPPORTUNITIES = [];
export const usePulseOpportunitiesWidget = ()=>{
    var _ref, _ref1, _ref2;
    const { browser } = useIoContext();
    const logBiEvent = useLogBIEvent();
    const { feed, isLoading, error, refresh } = usePulseOpportunities({
        refreshOnHookMount: true
    });
    const { clients } = useClients();
    const [expandedFeedId, setExpandedFeedId] = useState(null);
    const [hasFailed, setHasFailed] = useState(false);
    const rootRef = useRef(null);
    const hasEmittedView = useRef(false);
    const generatedAt = (_ref = feed === null || feed === void 0 ? void 0 : feed.generatedAt) !== null && _ref !== void 0 ? _ref : null;
    const { recognised, unrecognised } = partitionByRecognisedKind((_ref1 = feed === null || feed === void 0 ? void 0 : feed.opportunities) !== null && _ref1 !== void 0 ? _ref1 : NO_OPPORTUNITIES);
    const isExpanded = generatedAt != null && expandedFeedId === generatedAt;
    const { visible, hiddenCount } = selectVisibleEntries(recognised, OPPORTUNITY_PREFIX_SIZE, isExpanded);
    const skippedReport = unrecognisedKindReport(unrecognised);
    const clientNamesById = useMemo(()=>new Map(clients.map((client)=>[
                client.clientId(),
                client.name()
            ])), [
        clients
    ]);
    useEffect(()=>{
        const element = rootRef.current;
        if (element != null) {
            return browser.observeElementVisibility(element, ()=>{
                if (!hasEmittedView.current) {
                    hasEmittedView.current = true;
                    logBiEvent({
                        type: PULSE_OPPORTUNITIES_VIEW_EVENT_TYPE,
                        entity_type: 'widget',
                        entity_name: PULSE_OPPORTUNITIES_ENTITY_NAME
                    });
                }
            });
        }
    }, [
        logBiEvent
    ]);
    useEffect(()=>{
        if (skippedReport != null) {
            browser.reportError(new Error(skippedReport));
        }
    }, [
        skippedReport
    ]);
    useEffect(()=>{
        if (error != null) {
            setHasFailed(true);
        }
    }, [
        error
    ]);
    useEffect(()=>{
        if (feed != null) {
            setHasFailed(false);
        }
    }, [
        feed
    ]);
    function toggleExpanded() {
        setExpandedFeedId(isExpanded ? null : generatedAt);
    }
    const state = derivePulseOpportunitiesState({
        hasFailedWithNothingToShow: hasFailed && feed == null,
        isLoading,
        isEmpty: recognised.length === 0
    });
    return {
        rootRef,
        state,
        visible,
        hiddenCount,
        isExpanded,
        lastSyncedAt: (_ref2 = feed === null || feed === void 0 ? void 0 : feed.lastSyncedAt) !== null && _ref2 !== void 0 ? _ref2 : null,
        locale: i18next.language,
        clientNamesById,
        toggleExpanded,
        retry: ()=>void refresh({
                throwOnError: false
            })
    };
};
